import {
  type FC,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { hasValue } from '@core/utils/equality';
import type { NotificationScope, NotificationScopeObject } from '@core/utils/notifications';
import { useNotificationScopeObject } from '@web/components/NotificationCenter/useNotificationScopeObject';
import { useNotificationsSocket } from '@web/components/NotificationCenter/useNotificationsSocket';

type NotificationPanelContextValue = {
  isOpen: boolean;
  scopeObject: NotificationScopeObject | null;
  scope: NotificationScope;
  pendingArrivalIds: ReadonlySet<string>;
  markArrivalShown: (id: string) => void;
  setScope: (scope: NotificationScope) => void;
  open: (scope?: NotificationScope) => void;
  close: () => void;
  toggle: () => void;
};

const NotificationPanelContext = createContext<NotificationPanelContextValue | null>(null);

export const useNotificationPanel = (): NotificationPanelContextValue => {
  const ctx = useContext(NotificationPanelContext);
  if (ctx === null) {
    throw new Error('useNotificationPanel must be used within NotificationPanelProvider');
  }
  return ctx;
};

type Props = { children: ReactNode };

export const NotificationPanelProvider: FC<Props> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scope, setScope] = useState<NotificationScope>('all');

  const { pendingArrivalIds, markArrivalShown, clearPendingArrivals } =
    useNotificationsSocket(isOpen);

  const scopeObject = useNotificationScopeObject();
  const scopeObjectKey = !hasValue(scopeObject) ? null : `${scopeObject.type}-${scopeObject.id}`;

  useEffect(() => {
    setScope('all');
  }, [scopeObjectKey]);

  const open = useCallback((nextScope: NotificationScope = 'all'): void => {
    setScope(nextScope);
    setIsOpen(true);
  }, []);

  const close = useCallback((): void => {
    setIsOpen(false);
    setScope('all');
    clearPendingArrivals();
  }, [clearPendingArrivals]);

  const toggle = useCallback((): void => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, close, open]);

  const value = useMemo<NotificationPanelContextValue>(
    () => ({
      isOpen,
      scopeObject,
      scope,
      pendingArrivalIds,
      markArrivalShown,
      setScope,
      open,
      close,
      toggle,
    }),
    [isOpen, scopeObject, scope, pendingArrivalIds, markArrivalShown, open, close, toggle],
  );

  return (
    <NotificationPanelContext.Provider value={value}>{children}</NotificationPanelContext.Provider>
  );
};
